import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { MatIcon } from "@angular/material/icon";
import { catchError, forkJoin, of } from "rxjs";
import {
  DashboardGraphItem,
  DashboardKpi,
  GRAPH_COLORS,
  RangoPeriodo,
  buildDonutStyle,
  normalizarGraficas,
  obtenerRangoPeriodo,
  porcentaje,
  textoAFecha,
  toNumber,
} from "src/app/dashboard/shared/dashboard-graficas";
import { PeriodoDashboardComponent } from "src/app/dashboard/shared/periodo-dashboard/periodo-dashboard.component";
import { PestanaDashboard } from "src/app/dashboard/shared/pestana-dashboard";
import { MatrizRiesgoService } from "src/app/matriz-riesgos-ia/matriz-riesgo.service";
import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import {
  Matriz,
  MatrizActivo,
  MatrizRes,
  MatrizRiesgoRes,
  RiesgoProceso,
  RiesgoProcesoRes,
} from "src/app/shared/model/iso42001/matriz-riesgo.model";
import {
  CorteReciente,
  PendientesRiesgos,
  RiesgosDashboard,
  RiesgosDashboardService,
} from "./riesgos-dashboard.service";

/** Una fila de "riesgos y activos por matriz". */
interface MatrizCarga {
  nombre: string;
  riesgos: number;
  activos: number;
}

/** Una celda del mapa de calor probabilidad x impacto (severidad inicial). */
interface CeldaCalor {
  prob: number;
  impacto: number;
  severidad: number;
  banda: string;
  total: number;
}

interface FilaCalor {
  prob: number;
  celdas: CeldaCalor[];
}

/** Bandas de iso42001_cat_clasificacion_severidad, en orden; los colores son fijos. */
const BANDAS = ["Baja", "Media", "Alta", "Critico"];
const BANDA_COLORS: Record<string, string> = {
  Baja: "#00897b",
  Media: "#1565c0",
  Alta: "#ef6c00",
  Critico: "#d32f2f",
};
const NIVELES = [1, 2, 3, 4];

/**
 * Pestaña "Riesgos" del Dashboard de Inicio (ISO/IEC 42001): matrices,
 * riesgos de proceso, activos IA y cortes de seguimiento.
 *
 * Lo que es FOTO de hoy (riesgos, bandas, activos, acciones, mapa de calor)
 * sale de los endpoints del modulo; lo que depende del PERIODO (cortes por
 * mes, ultimos cortes, riesgos sin corte) sale de gestionRiesgos/dashboard.do.
 * El modulo es global (sin tenant): la pestaña se gatea con las hojas
 * gestionRiesgos / matrizRiesgosIA y el backend exige las mismas.
 * Consulta al montarse; el cascaron (dashini) la vuelve a consultar con
 * actualizar().
 */
@Component({
  selector: "app-dashboard-riesgos",
  standalone: true,
  templateUrl: "./dashboard-riesgos.component.html",
  styleUrls: ["../../shared/dashboard-tarjetas.css", "./dashboard-riesgos.component.css"],
  imports: [CommonModule, MatIcon, PeriodoDashboardComponent],
})
export class DashboardRiesgosComponent implements OnInit, PestanaDashboard {
  readonly periodoInicial = "30";
  readonly impactos = NIVELES;

  rango: RangoPeriodo = obtenerRangoPeriodo(this.periodoInicial);
  rangoInicio: Date | null = null;
  rangoFin: Date | null = null;
  errorCarga = "";

  kpis: DashboardKpi[] = [];
  /** Dona: siempre las cuatro bandas, aunque valgan 0, para que la leyenda no baile. */
  bandaItems: DashboardGraphItem[] = [];
  totalRiesgos = 0;
  /**
   * Denominador de la dona y de su leyenda: los riesgos que SI caen en
   * una banda. Los que llegan sin valoracion inicial no cuentan aqui (ni
   * en el mapa de calor), asi que puede ser menor que totalRiesgos.
   */
  totalBandas = 0;
  /** La consulta de riesgos de proceso no llego: los ceros de la foto no son ceros reales. */
  errorRiesgos = false;
  matrizItems: MatrizCarga[] = [];
  matrizMax = 0;
  filasCalor: FilaCalor[] = [];
  mesItems: DashboardGraphItem[] = [];
  mesMax = 0;
  totalCortes = 0;
  cortesRecientes: CorteReciente[] = [];

  canGestion = false;
  canMatrizIA = false;

  private cargandoDatos = false;
  /** id de la matriz historica de IA (factory matrizRiesgosIA); 1 mientras no se sepa otra cosa. */
  private idMatrizIA = 1;
  private readonly destroyRef = inject(DestroyRef);
  /** true desde que la pestaña se destruye; guarda el repintado (ver repintar()). */
  private destruido = false;

  constructor(
    private matrizService: MatrizRiesgoService,
    private dashboardService: RiesgosDashboardService,
    private authService: AuthService,
    private router: Router,
    // La vista NO se repinta sola cuando llega la respuesta HTTP: en este
    // arranque zone.js no queda parcheando XMLHttpRequest, asi que el
    // estado cambia y el DOM se queda en "Actualizando indicadores..."
    // hasta el siguiente clic. Se pide el repintado a mano, el mismo
    // patron que usa el resto de la casa (asistencia/mi-asistencia).
    // NO quitar por "innecesario".
    private cdr: ChangeDetectorRef,
  ) {
    // Se registra ANTES de cualquier suscripcion: los callbacks de
    // DestroyRef corren en orden de registro, asi que cuando
    // takeUntilDestroyed cierre el stream al destruir la pestaña (y con
    // ello dispare su complete) esto ya vale true.
    this.destroyRef.onDestroy(() => (this.destruido = true));
  }

  ngOnInit() {
    const menus = this.authService.getMenuList();
    this.canGestion = tieneAccesoMenu(menus, "RIESGOS/gestionRiesgos");
    this.canMatrizIA = tieneAccesoMenu(menus, "RIESGOS/matrizRiesgosIA");
    this.aplicarRango(obtenerRangoPeriodo(this.periodoInicial));
    this.consultar();
  }

  get cargando(): boolean {
    return this.cargandoDatos;
  }

  actualizar(): void {
    this.consultar();
  }

  onPeriodo(rango: RangoPeriodo): void {
    this.aplicarRango(rango);
    this.consultar();
  }

  consultar(): void {
    this.cargandoDatos = true;
    this.errorCarga = "";

    forkJoin({
      matrices: this.matrizService.consultarMatrices({ estatus: "ACTIVO" }).pipe(catchError(() => of<MatrizRes | null>(null))),
      riesgos: this.matrizService.consultarRiesgos({ estatus: "ACTIVO" }).pipe(catchError(() => of<RiesgoProcesoRes | null>(null))),
      activos: this.matrizService.consultar({ estatus: "ACTIVO" }).pipe(catchError(() => of<MatrizRiesgoRes | null>(null))),
      pendientes: this.dashboardService.consultarPendientes().pipe(catchError(() => of<PendientesRiesgos | null>(null))),
      tablero: this.dashboardService.dashboard(this.rango).pipe(catchError(() => of<RiesgosDashboard | null>(null))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.pintar(res);
          this.repintar();
        },
        error: (e) => {
          console.error("Error al cargar dashboard de riesgos:", e);
          // RxJS no llama a complete despues de un error: hay que apagar la
          // carga aqui o la tarjeta "Actualizando indicadores..." se queda.
          this.cargandoDatos = false;
          this.errorCarga = "No fue posible cargar la informacion de riesgos.";
          this.repintar();
        },
        complete: () => {
          this.cargandoDatos = false;
          this.repintar();
        },
      });
  }

  /**
   * Repinta la pestaña. detectChanges() sobre una vista ya destruida
   * truena, y el complete SI llega al destruir (takeUntilDestroyed cierra
   * el stream desde el propio onDestroy), de ahi el guardia.
   */
  private repintar(): void {
    if (this.destruido) {
      return;
    }
    // markForCheck ADEMAS de detectChanges: detectChanges refresca solo
    // ESTA vista, y el cascaron (dashini) lee el `cargando` de la pestaña
    // para habilitar su boton "Actualizar". Sin marcar el camino hacia la
    // raiz ese enlace se queda con el valor viejo: el boton se queda
    // deshabilitado para siempre y en desarrollo sale un NG0100.
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  getDonutStyle(): Record<string, string> {
    if (this.totalBandas <= 0) {
      return { background: "#eef1f5" };
    }
    return buildDonutStyle(this.bandaItems, this.totalBandas);
  }

  getBandaPercent(item: DashboardGraphItem): number {
    return porcentaje(item.valor, this.totalBandas);
  }

  /** Solo el ancho: el color va por clase (.riesgos-bar--*) para que el tema lo mande. */
  getMatrizBarStyle(valor: number): Record<string, string> {
    return {
      width: `${porcentaje(valor, this.matrizMax)}%`,
    };
  }

  getMesBarStyle(item: DashboardGraphItem, index: number): Record<string, string> {
    return {
      width: `${porcentaje(item.valor, this.mesMax)}%`,
      background: item.color || GRAPH_COLORS[index % GRAPH_COLORS.length],
    };
  }

  getCeldaClass(celda: CeldaCalor): string {
    const vacia = celda.total === 0 ? " riesgos-heat__cell--vacia" : "";
    return `riesgos-heat__cell--${celda.banda.toLowerCase()}${vacia}`;
  }

  getTipoClass(corte: CorteReciente): string {
    return corte?.tipo === "Activo" ? "ticket-status--proceso" : "ticket-status--atendida";
  }

  /** Una tarjeta con ruta (hoy, «Propuestas pendientes» -> «Cambios por aprobar»). */
  abrirKpi(kpi: DashboardKpi): void {
    if (kpi.ruta) {
      this.router.navigate([kpi.ruta]);
    }
  }

  /** Al tablero de matrices; si solo tiene la matriz IA, a esa. */
  abrirGestion(): void {
    if (this.canGestion) {
      this.router.navigate(["/RIESGOS/gestionRiesgos"]);
    } else if (this.canMatrizIA) {
      this.router.navigate(["/RIESGOS/matrizRiesgosIA"]);
    }
  }

  /** A la matriz del corte: matriz/:idMatriz con el modulo, o la ruta historica de la matriz IA. */
  abrirCorte(corte: CorteReciente): void {
    if (!corte?.idMatriz) {
      return;
    }
    if (this.canGestion) {
      this.router.navigate(["/RIESGOS/matriz", corte.idMatriz]);
    } else if (this.canMatrizIA && corte.idMatriz === this.idMatrizIA) {
      this.router.navigate(["/RIESGOS/matrizRiesgosIA"]);
    }
  }

  /** Para "Ver todos": abrirGestion tiene salida con cualquiera de las dos hojas. */
  get puedeAbrir(): boolean {
    return this.canGestion || this.canMatrizIA;
  }

  /**
   * Para el boton de CADA fila: quien solo tiene matrizRiesgosIA no puede
   * abrir los cortes de las demas matrices (hay hasta cuatro y solo la
   * historica lleva esa hoja), asi que ahi el boton no se pinta en lugar
   * de pintarse y no hacer nada.
   */
  puedeAbrirCorte(corte: CorteReciente): boolean {
    if (!corte?.idMatriz) {
      return false;
    }
    return this.canGestion || (this.canMatrizIA && corte.idMatriz === this.idMatrizIA);
  }

  private aplicarRango(rango: RangoPeriodo): void {
    this.rango = rango;
    this.rangoInicio = rango.inicio ? textoAFecha(rango.inicio) : null;
    this.rangoFin = rango.fin ? textoAFecha(rango.fin) : null;
  }

  private pintar(res: {
    matrices: MatrizRes | null;
    riesgos: RiesgoProcesoRes | null;
    activos: MatrizRiesgoRes | null;
    pendientes: PendientesRiesgos | null;
    tablero: RiesgosDashboard | null;
  }): void {
    const matricesRes = this.sinError(res.matrices);
    const riesgosRes = this.sinError(res.riesgos);
    const activosRes = this.sinError(res.activos);
    const pendientesRes = this.sinError(res.pendientes);
    const tablero = res.tablero;

    if (!matricesRes && !riesgosRes && !activosRes && !pendientesRes && !tablero) {
      this.errorCarga = "No fue posible cargar la informacion de riesgos.";
    }

    // Solo matrices ACTIVO: si esa consulta fallo no se filtra, para no vaciar la pestaña.
    const matrices: Matriz[] | null = matricesRes ? matricesRes.matrices || [] : null;
    const idsActivas = matrices ? new Set(matrices.map((m) => m.idMatriz)) : null;
    const riesgos = (riesgosRes?.riesgos || []).filter((r) => !idsActivas || idsActivas.has(r.idMatriz));
    const activos = (activosRes?.activos || []).filter((a) => !idsActivas || idsActivas.has(a.idMatriz));
    const matrizIA = (matrices || []).find((m) => m.factoryMenu === "matrizRiesgosIA");
    this.idMatrizIA = matrizIA?.idMatriz || 1;

    this.errorRiesgos = !riesgosRes;
    this.totalRiesgos = riesgos.length;
    this.bandaItems = BANDAS.map((banda) => ({
      nombre: banda,
      valor: riesgos.filter((r) => this.bandaDeRiesgo(r) === banda).length,
      color: BANDA_COLORS[banda],
    }));
    // Los riesgos sin valoracion inicial no entran en ninguna banda ni en el
    // mapa de calor: el denominador de la dona es lo graficado, no el total.
    this.totalBandas = this.bandaItems.reduce((total, item) => total + item.valor, 0);
    this.matrizItems = this.construirMatrices(matrices, riesgosRes ? riesgos : null, activosRes ? activos : null);
    this.matrizMax = Math.max(...this.matrizItems.map((item) => Math.max(item.riesgos, item.activos)), 0);
    this.filasCalor = this.construirCalor(riesgos);

    const cortesPorMes = normalizarGraficas(tablero?.cortesPorMes || [], false);
    this.totalCortes = cortesPorMes.reduce((total, item) => total + item.valor, 0);
    this.mesItems = cortesPorMes.slice(-8);
    this.mesMax = Math.max(...this.mesItems.map((item) => item.valor), 0);
    this.cortesRecientes = tablero?.ultimosCortes || [];

    this.kpis = this.construirKpis(matrices, riesgosRes ? riesgos : null, activosRes ? activos : null,
      pendientesRes, tablero);
  }

  /** Las respuestas del modulo traen codigoError en el cuerpo; con el, cuentan como fallidas. */
  private sinError<T extends { codigoError?: string }>(res: T | null): T | null {
    return res && !res.codigoError ? res : null;
  }

  /**
   * La banda que calculo el backend; si no vino, la de la severidad
   * inicial. Un riesgo SIN valorar (prob_inicial / impacto_inicial en
   * NULL) no cae en "Baja": no entra en ninguna banda, igual que en la
   * hoja RSK del modulo, y tampoco aparece en el mapa de calor.
   */
  private bandaDeRiesgo(riesgo: RiesgoProceso): string {
    if (riesgo.clasificacionInicial) {
      return riesgo.clasificacionInicial;
    }
    const severidad = toNumber(riesgo.severidadInicial) || toNumber(riesgo.probInicial) * toNumber(riesgo.impactoInicial);
    return severidad > 0 ? this.bandaDe(severidad) : "";
  }

  /** Bandas del catalogo: 1-4 Baja, 5-8 Media, 9-12 Alta, 13-16 Critico. */
  private bandaDe(severidad: number): string {
    if (severidad >= 13) {
      return "Critico";
    }
    if (severidad >= 9) {
      return "Alta";
    }
    if (severidad >= 5) {
      return "Media";
    }
    return "Baja";
  }

  private construirMatrices(matrices: Matriz[] | null, riesgos: RiesgoProceso[] | null, activos: MatrizActivo[] | null): MatrizCarga[] {
    // Sin el listado de matrices se arma con las que aparecen en los riesgos y activos.
    const base: Matriz[] = matrices || this.matricesDesde(riesgos || [], activos || []);
    return base
      .map((m) => ({
        nombre: m.nombre || `Matriz ${m.idMatriz}`,
        // Se cuenta sobre las listas (solo ACTIVO) para cuadrar con los KPIs;
        // totalRiesgos/totalActivos del tablero cuentan tambien INACTIVO.
        riesgos: riesgos ? riesgos.filter((r) => r.idMatriz === m.idMatriz).length : toNumber(m.totalRiesgos),
        activos: activos ? activos.filter((a) => a.idMatriz === m.idMatriz).length : toNumber(m.totalActivos),
      }))
      .sort((a, b) => b.riesgos + b.activos - (a.riesgos + a.activos))
      .slice(0, 8);
  }

  private matricesDesde(riesgos: RiesgoProceso[], activos: MatrizActivo[]): Matriz[] {
    const ids = new Set<number>();
    riesgos.forEach((r) => r.idMatriz && ids.add(r.idMatriz));
    activos.forEach((a) => a.idMatriz && ids.add(a.idMatriz));
    return [...ids].map((idMatriz) => ({ idMatriz }));
  }

  /** Filas de probabilidad 4..1 (arriba la mas alta) por columnas de impacto 1..4. */
  private construirCalor(riesgos: RiesgoProceso[]): FilaCalor[] {
    return [...NIVELES].reverse().map((prob) => ({
      prob,
      celdas: NIVELES.map((impacto) => ({
        prob,
        impacto,
        severidad: prob * impacto,
        banda: this.bandaDe(prob * impacto),
        total: riesgos.filter((r) => toNumber(r.probInicial) === prob && toNumber(r.impactoInicial) === impacto).length,
      })),
    }));
  }

  /**
   * riesgos y activos llegan en null cuando SU consulta fallo: sus
   * tarjetas muestran "-" y "Sin datos del servidor", como ya hacen las
   * que dependen de dashboard.do. Un cero de verdad y una consulta caida
   * no pueden leerse igual.
   */
  private construirKpis(
    matrices: Matriz[] | null,
    riesgos: RiesgoProceso[] | null,
    activos: MatrizActivo[] | null,
    pendientes: PendientesRiesgos | null,
    tablero: RiesgosDashboard | null,
  ): DashboardKpi[] {
    const listaRiesgos = riesgos || [];
    const listaActivos = activos || [];
    const altas = listaRiesgos.filter((r) => ["Alta", "Critico"].includes(this.bandaDeRiesgo(r))).length;
    const reevaluados = listaRiesgos.filter((r) => r.severidadInicial != null && r.severidadReevaluada != null);
    const reduccion = reevaluados.reduce((total, r) => total + (toNumber(r.severidadInicial) - toNumber(r.severidadReevaluada)), 0);
    const media = reevaluados.length > 0 ? reduccion / reevaluados.length : 0;
    const activosAltos = listaActivos.filter((a) => ["Alto", "Muy Alto"].includes(a.clasificacionActual || "")).length;

    const acciones = normalizarGraficas(tablero?.accionesPorEstado || []);
    const aplicadas = this.valorDe(acciones, "APLICADA");
    const vencidas = this.valorDe(acciones, "VENCIDA");
    const porAplicar = this.valorDe(acciones, "POR APLICAR") + vencidas;

    const propuestas = (pendientes?.pendientes || []).filter((p) => !p.estatus || p.estatus === "PENDIENTE").length;

    return [
      {
        label: "Riesgos de proceso",
        value: riesgos ? this.totalRiesgos : "-",
        meta: !riesgos
          ? "Sin datos del servidor"
          : matrices ? `${matrices.length} matrices activas (hoy)` : "Foto de hoy",
        icon: "policy",
        className: "ticket-kpi--total",
      },
      {
        label: "En banda Alta/Critico",
        value: riesgos ? altas : "-",
        meta: riesgos ? `${porcentaje(altas, this.totalBandas)}% de los valorados (hoy)` : "Sin datos del servidor",
        icon: "local_fire_department",
        className: "ticket-kpi--active",
      },
      {
        label: "Reduccion media de severidad",
        value: !riesgos
          ? "-"
          : reevaluados.length > 0 ? `${media > 0 ? "-" : media < 0 ? "+" : ""}${Math.abs(media).toFixed(1)} pts` : "0 pts",
        meta: !riesgos
          ? "Sin datos del servidor"
          : reevaluados.length > 0 ? `${reevaluados.length} de ${this.totalRiesgos} reevaluados` : "Sin reevaluaciones",
        icon: "trending_down",
        className: "ticket-kpi--done",
      },
      {
        label: "Activos IA en riesgo Alto/Muy Alto",
        value: activos ? activosAltos : "-",
        meta: activos ? `${listaActivos.length} activos evaluados (hoy)` : "Sin datos del servidor",
        icon: "smart_toy",
        className: "ticket-kpi--time",
      },
      {
        label: "Acciones por aplicar",
        value: tablero ? porAplicar : "-",
        meta: tablero ? `${vencidas} vencidas / ${aplicadas} aplicadas (hoy)` : "Sin datos del servidor",
        icon: "task_alt",
        className: "ticket-kpi--active",
      },
      {
        label: "Cortes en el periodo",
        value: tablero ? this.totalCortes : "-",
        meta: tablero ? "Riesgos de proceso + activos IA" : "Sin datos del servidor",
        icon: "event_available",
        className: "ticket-kpi--done",
      },
      {
        label: "Sin corte en el periodo",
        value: tablero ? toNumber(tablero.riesgosSinCorte) : "-",
        meta: tablero ? `de ${toNumber(tablero.riesgosActivos)} riesgos activos` : "Sin datos del servidor",
        icon: "event_busy",
        className: "ticket-kpi--muted",
      },
      {
        label: "Propuestas pendientes",
        value: pendientes ? propuestas : "-",
        meta: !pendientes
          ? "Sin datos del servidor"
          : pendientes.puedeEditar
            ? (pendientes.aprobacionActiva ? "Esperan revisión · abrir la bandeja" : "Aprobación apagada · ver la bandeja")
            : (pendientes.aprobacionActiva ? "Esperan revisión" : "Aprobación apagada"),
        icon: "pending_actions",
        className: "ticket-kpi--muted",
        // La bandeja «Cambios por aprobar» es de quien puede aceptar y
        // descartar (el perfil de edición de la matriz).
        ruta: pendientes && pendientes.puedeEditar ? "/RIESGOS/cambiosPorAprobar" : undefined,
      },
    ];
  }

  private valorDe(items: DashboardGraphItem[], nombre: string): number {
    return items.filter((item) => item.nombre === nombre).reduce((total, item) => total + item.valor, 0);
  }
}
